import { Autocomplete, SelectOption } from "@flixbus-phx/marketplace-common";
import * as React from "react";
import { useIntl } from "react-intl";
import InputApiError from "../../../../shared/components/inputApiError/InputApiError";
import getFindBookingQueryVars from "../../../../shared/helpers/getFindBookingQueryVars/getFindBookingQueryVars";
import useRideFilter, { RideFilter, rideFilterVar } from "../../../../state/useRideFilter/useRideFilter";
import { useFindRideFilterDataQuery } from "../../api/operations.generated";
import getOptionsForFilter from "../../helpers/getOptionsForFilter/getOptionsForFilter";
import RideFilterType from "../../types";

export type RideFilterSelectProps = {
  type: RideFilterType;
  onChange?: (value: RideFilter["departureStationId"] | RideFilter["lineId"] | RideFilter["departureTime"]) => void;
  isSearchable?: boolean;
  value?: RideFilter["departureStationId"] | RideFilter["lineId"] | RideFilter["departureTime"];
};

const RideFilterSelect: React.FC<RideFilterSelectProps> = ({ type, onChange, isSearchable, value }) => {
  const [rideFilter, setRideFilter] = useRideFilter(rideFilterVar);
  const intl = useIntl();

  const { data, loading, error } = useFindRideFilterDataQuery({
    variables: getFindBookingQueryVars(rideFilter, false, true),
    fetchPolicy: "cache-first",
  });

  const handleSelect = (selectedOption: SelectOption) => {
    if (onChange && value !== undefined) {
      onChange(selectedOption.value);
    } else {
      const changedFilter = { ...rideFilter };
      changedFilter[type] = selectedOption.value;
      setRideFilter(changedFilter);
    }
  };

  if (error) {
    return (
      <InputApiError
        label={intl.formatMessage({ id: `ride.filter.${type}` })}
        info={intl.formatMessage({ id: `ride.filter.${type}.errorMessage` })}
      />
    );
  }

  const options = data?.findFilterValues
    ? [{ value: "", label: intl.formatMessage({ id: "ride.filter.select.default" }) }].concat(
        getOptionsForFilter(data.findFilterValues, type)
      )
    : [];

  return (
    <Autocomplete
      label={intl.formatMessage({ id: `ride.filter.${type}` })}
      options={options}
      initialValue={
        value !== undefined
          ? options.find((option) => option.value === value)
          : options.find((option) => option.value === rideFilter[type])
      }
      onValueSelected={handleSelect}
      startsOnInputValueLength={0}
      isLoading={loading}
      isSearchable={isSearchable}
    />
  );
};

export default RideFilterSelect;
